import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryVideos = videos.filter((v) => v.category === slug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 91视频网站 | 在线视频平台`;
    } else {
      document.title = '分类 - 91视频网站';
    }
    window.scrollTo(0, 0);
  }, [slug, category]);

  if (!category) {
    return (
      <main className="pt-20 px-4 md:px-6 lg:px-8 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的视频分类不存在。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: category.name },
  ];

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 91视频网站`,
    description: category.description,
    url: `http://ewqivah.cn/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: v.title,
        url: `http://ewqivah.cn/category/${slug}`,
      })),
    },
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />

      <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 lg:px-8 bg-foreground-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/80 to-foreground-950" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-3xl md:text-5xl font-black text-white mt-4 mb-3">{category.name}</h1>
          <p className="text-foreground-400 text-base md:text-lg max-w-xl">{category.description}</p>
          <div className="flex items-center gap-4 mt-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-video-line text-primary-500" />
              {category.videoCount.toLocaleString()} 部视频
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-amber-500" />
              每日更新
            </span>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-7xl mx-auto">
          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {categoryVideos.map((video) => (
                <div
                  key={video.id}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.image}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/20 transition-colors flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-primary-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <i className="ri-play-fill text-xl" />
                      </span>
                    </div>
                    <span className="absolute bottom-3 right-3 px-2 py-1 bg-foreground-950/70 text-white text-xs rounded-md">
                      {video.duration}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-foreground-900 font-semibold text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {video.views}
                      </span>
                      <span className="flex items-center gap-1 text-amber-500">
                        <i className="ri-star-fill" />
                        {video.rating}
                      </span>
                    </div>
                    {video.description && (
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-video-off-line text-4xl text-foreground-400 mb-4" />
              <p className="text-foreground-500">该分类暂无视频内容</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="w-full max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">其他分类</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.filter((c) => c.slug !== slug).map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.slug}`}
                className="group relative aspect-[16/9] rounded-xl overflow-hidden"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-foreground-950/50 group-hover:bg-foreground-950/40 transition-colors flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{c.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}